import React, { useState, useEffect } from 'react';
import { Users, Plus, Calendar, X, Download, History } from 'lucide-react';
import api from '../services/api';
import LedgerModal from './LedgerModal';

export default function StaffList({ tenantId }) {
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedLedgerEmp, setSelectedLedgerEmp] = useState(null);
  const [newEmployee, setNewEmployee] = useState({ name: '', email: '', role: 'employee', hireDate: '', categoryId: '', initialBalance: 0 });
  const [submitting, setSubmitting] = useState(false);

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Category', 'Hire Date', 'Current Balance'];
    const rows = employees.map(emp => {
      const category = categories.find(c => c.id === emp.categoryId);
      return [
        emp.name,
        emp.email,
        emp.role,
        category ? category.name : 'None',
        emp.hireDate,
        emp.currentBalance || 0
      ];
    });

    let csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n"
      + rows.map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `staff_balances_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  async function fetchData() {
    setLoading(true);
    try {
      const [empRes, catRes] = await Promise.allSettled([
        api.get(`/tenants/${tenantId}/employees`),
        api.get(`/tenants/${tenantId}/admin/categories`) // HR Admin route
      ]);
      
      if (empRes.status === 'fulfilled') setEmployees(empRes.value.data.data || []);
      if (catRes.status === 'fulfilled') setCategories(catRes.value.data.data || []);
    } catch (err) {
      console.error('Failed to fetch data', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [tenantId]);

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/tenants/${tenantId}/employees`, newEmployee);
      await fetchData();
      setShowModal(false);
      setNewEmployee({ name: '', email: '', role: 'employee', hireDate: '', categoryId: '', initialBalance: 0 });
    } catch (err) {
      alert('Failed to add employee: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={20} color="var(--primary-500)" />
            Staff & Roles
          </h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Manage employee profiles and leave quotas</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={exportToCSV}>
            <Download size={16} />
            Export CSV
          </button>
          <button className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={() => setShowModal(true)}>
            <Plus size={16} />
            Onboard Employee
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading staff data...</div>
      ) : employees.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
          <p>No employees found. Add your first staff member.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={{ padding: '12px' }}>Name</th>
                <th style={{ padding: '12px' }}>Role & Category</th>
                <th style={{ padding: '12px' }}>Hire Date</th>
                <th style={{ padding: '12px' }}>Current Balance</th>
                <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => {
                const category = categories.find(c => c.id === emp.categoryId);
                return (
                  <tr key={emp.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{emp.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{emp.email}</div>
                    </td>
                    <td style={{ padding: '12px', textTransform: 'capitalize' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ 
                          padding: '4px 8px', 
                          borderRadius: '4px', 
                          background: emp.role === 'hr_admin' ? 'var(--primary-50)' : 'var(--bg-secondary)',
                          color: emp.role === 'hr_admin' ? 'var(--primary-600)' : 'var(--text-primary)',
                          fontSize: '0.8rem',
                          fontWeight: 500
                        }}>
                          {emp.role.replace('_', ' ')}
                        </span>
                        {category && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', border: '1px solid var(--border-color)', padding: '2px 6px', borderRadius: '4px' }}>
                            {category.name} ({category.annualQuota}d)
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-muted)' }}>
                      {emp.hireDate}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
                        <Calendar size={14} color="var(--primary-500)" />
                        {emp.currentBalance !== undefined ? emp.currentBalance : 0} Days
                      </div>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button className="btn btn-outline" style={{ padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem' }} onClick={() => setSelectedLedgerEmp(emp)}>
                        <History size={14} /> Audit Ledger
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedLedgerEmp && (
        <LedgerModal 
          tenantId={tenantId} 
          employee={selectedLedgerEmp} 
          onClose={() => { setSelectedLedgerEmp(null); fetchData(); }} 
        />
      )}

      {/* Onboarding Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem' }}>
                <Users size={20} color="var(--primary-500)" />
                Onboard New Employee
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAddEmployee}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input 
                  type="email" 
                  className="form-input" 
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">System Role</label>
                  <select 
                    className="form-input" 
                    value={newEmployee.role}
                    onChange={(e) => setNewEmployee({...newEmployee, role: e.target.value})}
                    required
                  >
                    <option value="employee">Employee</option>
                    <option value="manager">Manager (N+1)</option>
                    <option value="hr_admin">HR Admin</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Hire Date</label>
                  <input 
                    type="date" 
                    className="form-input" 
                    value={newEmployee.hireDate}
                    onChange={(e) => setNewEmployee({...newEmployee, hireDate: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Leave Category / Quota</label>
                  <select 
                    className="form-input" 
                    value={newEmployee.categoryId}
                    onChange={(e) => setNewEmployee({...newEmployee, categoryId: e.target.value})}
                    required
                  >
                    <option value="" disabled>Select a category...</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name} ({cat.annualQuota} days/yr)</option>
                    ))}
                  </select>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                    Determines the annual accrual limit.
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Initial Balance (Days)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    min="0"
                    step="0.5"
                    value={newEmployee.initialBalance}
                    onChange={(e) => setNewEmployee({...newEmployee, initialBalance: e.target.value})}
                    required
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                    Starting ledger balance.
                  </p>
                </div>
              </div>

              <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button 
                  type="button" 
                  className="btn btn-outline" 
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
